/**
 * PreferencesTab — clinical role, surgical team (hartchirurg only).
 */

import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router";
import { ApiError, authFetchJson } from "@/lib/api";

type SurgicalCategory =
  | "chirurgen"
  | "perfusionisten"
  | "instrumenterend_verpleegkundigen"
  | "anesthesisten";

type SurgicalTeam = Record<SurgicalCategory, string[]>;

interface UserSettings {
  role?: string;
  surgical_team?: SurgicalTeam;
}

const DEFAULT_TEAM: SurgicalTeam = {
  chirurgen: [],
  perfusionisten: [],
  instrumenterend_verpleegkundigen: [],
  anesthesisten: [],
};

const TEAM_CATEGORIES: Array<{ key: SurgicalCategory; label: string }> = [
  { key: "chirurgen", label: "Chirurgen" },
  { key: "perfusionisten", label: "Perfusionisten" },
  { key: "instrumenterend_verpleegkundigen", label: "Instrumenterend verpleegkundigen" },
  { key: "anesthesisten", label: "Anesthesisten" },
];

export function PreferencesTab() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [role, setRole] = useState("");
  const [surgicalTeam, setSurgicalTeam] = useState<SurgicalTeam>({ ...DEFAULT_TEAM });
  const [newTeamName, setNewTeamName] = useState<Record<SurgicalCategory, string>>({
    chirurgen: "",
    perfusionisten: "",
    instrumenterend_verpleegkundigen: "",
    anesthesisten: "",
  });

  const isHartchirurgie = role.toLowerCase().includes("chirurg");

  const flashSuccess = useCallback((msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 2500);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await authFetchJson<UserSettings>("/api/settings");
        setRole(data.role ?? "");
        setSurgicalTeam({ ...DEFAULT_TEAM, ...(data.surgical_team ?? {}) });
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          nav("/login");
          return;
        }
        setError(err instanceof Error ? err.message : "Kon instellingen niet laden.");
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [nav]);

  const save = useCallback(
    async (patch: Partial<UserSettings>) => {
      setSaving(true);
      setError("");
      try {
        await authFetchJson("/api/settings", {
          method: "PUT",
          body: JSON.stringify(patch),
        });
        flashSuccess("Opgeslagen");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Opslaan mislukt.");
      } finally {
        setSaving(false);
      }
    },
    [flashSuccess],
  );

  const changeRole = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value;
      setRole(next);
      await save({ role: next });
    },
    [save],
  );

  const addTeamMember = useCallback(
    async (category: SurgicalCategory) => {
      const name = newTeamName[category].trim();
      if (!name) return;
      const next: SurgicalTeam = {
        ...surgicalTeam,
        [category]: [...surgicalTeam[category], name],
      };
      setSurgicalTeam(next);
      setNewTeamName((prev) => ({ ...prev, [category]: "" }));
      await save({ surgical_team: next });
    },
    [newTeamName, surgicalTeam, save],
  );

  const removeTeamMember = useCallback(
    async (category: SurgicalCategory, index: number) => {
      const next: SurgicalTeam = {
        ...surgicalTeam,
        [category]: surgicalTeam[category].filter((_, i) => i !== index),
      };
      setSurgicalTeam(next);
      await save({ surgical_team: next });
    },
    [surgicalTeam, save],
  );

  if (loading) {
    return <p className="text-sm text-slate-500">Laden…</p>;
  }

  return (
    <div className="space-y-6">
      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="flex items-center gap-3 text-xs">
        {saving ? <span className="text-slate-400">Opslaan…</span> : null}
        {successMsg ? (
          <span className="rounded-full bg-green-100 px-2 py-0.5 font-medium text-green-700">
            ✓ {successMsg}
          </span>
        ) : null}
      </div>

      {/* Role */}
      <section className="glass-card rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="mb-3 text-lg font-bold text-slate-900">Rol</h2>
        <input
          type="text"
          value={role}
          onChange={(e) => void changeRole(e)}
          placeholder="bv. Cardioloog, Huisarts, Chirurg…"
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500"
        />
      </section>

      {/* Surgical team (hartchirurgie only) */}
      {isHartchirurgie ? (
        <section className="glass-card rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="mb-1 text-lg font-bold text-slate-900">Operatieteam</h2>
          <p className="mb-4 text-xs text-slate-500">
            Teamleden worden ingevoerd in de header van operatieverslagen.
          </p>
          <div className="space-y-4">
            {TEAM_CATEGORIES.map((cat) => (
              <div key={cat.key}>
                <h3 className="mb-1.5 text-sm font-semibold text-slate-700">{cat.label}</h3>
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {surgicalTeam[cat.key].length === 0 ? (
                    <span className="text-xs text-slate-400">Nog geen leden</span>
                  ) : null}
                  {surgicalTeam[cat.key].map((name, i) => (
                    <span
                      key={`${cat.key}-${i}`}
                      className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700"
                    >
                      {name}
                      <button
                        type="button"
                        onClick={() => void removeTeamMember(cat.key, i)}
                        className="text-slate-400 hover:text-red-600"
                        aria-label={`Verwijder ${name}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTeamName[cat.key]}
                    onChange={(e) =>
                      setNewTeamName((prev) => ({
                        ...prev,
                        [cat.key]: e.target.value,
                      }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        void addTeamMember(cat.key);
                      }
                    }}
                    placeholder="Voornaam Achternaam"
                    className="flex-1 rounded border border-slate-300 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-rose-500"
                  />
                  <button
                    type="button"
                    onClick={() => void addTeamMember(cat.key)}
                    disabled={!newTeamName[cat.key].trim()}
                    className="rounded bg-rose-500 px-3 py-1 text-xs font-semibold text-white hover:bg-rose-600 disabled:opacity-50"
                  >
                    Toevoegen
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

    </div>
  );
}
